import * as R from 'Router/routes';
import { ClientBlockedStatusState } from 'hooks/webapi/useClientBlockedStatus';
import { getClearUrlQueryString } from 'utils/getUrl';
import { getNextDRScreen } from 'utils/drRedirection';
import { isUserAffectedByDana } from 'pages/Dana/Dana';
import { isValidApplication } from 'utils/applicationUtility';
import {
  PENDING_BUNDLE_FEE,
  PENDING_BUNDLE_HOLD_PERIOD,
  PRODUCT_NUMBER_PARAM,
} from 'utils/constants';
import {
  useApplication,
  useAuth,
  useClient,
  useClientBlockedStatus,
  useLatestLoan,
} from './webapi';
import { useInsurancePolicy } from 'pages/InsurancePolicy/useInsurancePolicy';
import { useNavigate } from 'react-router-dom';
import { useRepeatedLoanNavigation } from 'hooks/useRepeatedLoanNavigation';
import { userIsNotIdentified } from 'utils/userIsNotIdentified';
import { verifyProductNumberLoginRoute } from 'utils/getProductNumber';
import useApplicationProposals from './webapi/useApplicationProposals';
import useClientDebtRestructuring from './webapi/useClientDebtRestructuring';
import useConditionalNavigation from 'hooks/utilities/useConditionalNavigation';

interface PropsRedirectUser {
  accessVerificationRequired?: boolean;
  fromTemporaryPassword?: boolean;
}

interface UseLoginRedirection {
  redirectUserAfterLogin: ({
    accessVerificationRequired,
    fromTemporaryPassword,
  }: PropsRedirectUser) => Promise<void>;
  redirectRouteDRAcceptedOrBlocked: (
    clientBlockedFromParent?: ClientBlockedStatusState,
  ) => string;
}

const useLoginRedirection = (): UseLoginRedirection => {
  const { fetchClient, postAnalyticsCookies } = useClient();
  const { fetchLatestLoan } = useLatestLoan();
  const { fetchApplication } = useApplication();
  const { clientBlockedStatus, fetchClientBlockedStatus } =
    useClientBlockedStatus();
  const { applicationProposals, fetchApplicationProposals } =
    useApplicationProposals();
  const { crossLoginRedirect } = useAuth();
  const { fetchDebtRestructuringData, isDebtRestructuring } =
    useClientDebtRestructuring();
  const { navigateToBestOfferOrInsuranceScreen } = useInsurancePolicy();
  const { getRepeatedLoginRedirection } = useRepeatedLoanNavigation();
  const { navigateToDashboard } = useConditionalNavigation();
  const navigate = useNavigate();

  const redirectUserAfterLogin = async ({
    accessVerificationRequired = false,
    fromTemporaryPassword = false,
  }: PropsRedirectUser) => {
    if (accessVerificationRequired) {
      navigate(`${R.TWO_FACTOR_AUTH}${window.location.search}`);
      return;
    }

    try {
      postAnalyticsCookies();
    } catch {}
    const debtRestructuringData = await fetchDebtRestructuringData();
    const client = await fetchClient();
    const application = await fetchApplication();

    // Persist the product kind so post-login redirects and URL building know which product was used
    if (application && isValidApplication(application, client)) {
      sessionStorage.setItem(
        PRODUCT_NUMBER_PARAM,
        application?.productInfo.kind,
      );
    }

    if (fromTemporaryPassword) {
      navigate(`${R.CHANGE_TEMPORARY_PASSWORD}${getClearUrlQueryString()}`);
    } else {
      if (client) {
        if (client.status === 'REGISTERED') {
          crossLoginRedirect({ application: application!, client });
          return;
        }

        // Verify login route used for default product number
        verifyProductNumberLoginRoute(client);

        let latestLoan;
        try {
          latestLoan = await fetchLatestLoan();
        } catch {}
        const isDanaUser = isUserAffectedByDana();
        if (isDanaUser) {
          navigate(R.DANA);
          return;
        }

        if (client.status === 'IDENTIFIED') {
          const urlParams = new URLSearchParams(
            decodeURIComponent(window.location.search),
          );
          const redirect = urlParams.get('redirect')
            ? decodeURIComponent(`${urlParams.get('redirect')}`)
            : undefined;
          if (redirect) {
            navigate(`${redirect}${getClearUrlQueryString()}`);
          } else {
            // Needed for rejected timer screen
            const clientBlocked =
              clientBlockedStatus || (await fetchClientBlockedStatus());
            if (clientBlocked?.blocked && !latestLoan) {
              navigate(R.REJECTED_TIMER);
              return;
            }

            fetchApplicationProposals(); // needed after redirection

            if ((debtRestructuringData?.status || '').includes('ACTIVE')) {
              navigate(getNextDRScreen(debtRestructuringData, latestLoan));
              return;
            }

            const isBestOfferExpired = !applicationProposals?.bestOfferProposal;

            if (application?.resolutionDetail === PENDING_BUNDLE_FEE) {
              navigate(R.BUNDLE_VIVUSPLUS_PENDING);
              return;
            }

            if (application?.resolutionDetail === PENDING_BUNDLE_HOLD_PERIOD) {
              navigate(R.BUNDLE_VIVUSPLUS_NO_CONFIRMED_AA);
              return;
            }

            if (
              application?.resolutionDetail === 'NEW_OFFER' &&
              !isBestOfferExpired
            ) {
              navigateToBestOfferOrInsuranceScreen(application);
              return;
            }

            if (application && isValidApplication(application, client)) {
              const userNotIdentified = userIsNotIdentified(client);
              if (userNotIdentified) {
                if (client?.hasAttachments) navigate(R.DOCUMENTS_PENDING);
                else navigate(R.CHECK_DOCUMENTS);
              } else if (!application.confirmed) {
                if (!latestLoan) {
                  const nextRoute = await getRepeatedLoginRedirection(
                    client,
                    application,
                  );
                  navigate(nextRoute);
                } else if (client?.additionalAmount)
                  navigate(
                    `${R.ADDITIONAL_AMOUNT_CONFIRM}${getClearUrlQueryString()}`,
                  );
              } else {
                navigate(`${R.DOCUMENTS_APPROVED}${getClearUrlQueryString()}`);
              }
            } else {
              navigateToDashboard(latestLoan);
            }
          }
        }
      }
    }
  };

  const redirectRouteDRAcceptedOrBlocked = (
    clientBlockedStatusFromParent?: ClientBlockedStatusState,
  ): string => {
    const clientBlockedToCheck =
      clientBlockedStatusFromParent || clientBlockedStatus;
    if (clientBlockedToCheck?.blocked) {
      return R.REJECTED_TIMER_FLOW;
    }

    if (isDebtRestructuring) {
      return R.DR_OFFER_ACCEPTED;
    }

    return R.ACTIVE_LOAN;
  };

  return {
    redirectUserAfterLogin,
    redirectRouteDRAcceptedOrBlocked,
  };
};

export default useLoginRedirection;
